import { PermutLogo } from "assets/logo"
import { Tabs, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import { Monitor, Smartphone } from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useEffect, useRef, useState } from "react"
import { UseFormReturn } from "react-hook-form"
import { WidgetConfigurationFormType } from "."
import { resetPreviewWidget } from "./lib/resetPreviewWidget"

type Props = {
  form: UseFormReturn<WidgetConfigurationFormType>
  language: string | null
  isLoading: boolean
}

const PREVIEW_MESSAGES: Record<string, { user: string; assistant: string }> = {
  en: {
    user: "Hello! Can you help me with something?",
    assistant: "Of course! I'm here to help. Feel free to ask me anything.",
  },
  es: {
    user: "¡Hola! ¿Me puedes ayudar con algo?",
    assistant: "¡Claro que sí! Estoy aquí para ayudarte. No dudes en preguntarme lo que quieras.",
  },
  ca: {
    user: "Hola! Em pots ajudar amb alguna cosa?",
    assistant: "Clar! Estic aquí per ajudar. No dubtis en preguntar-me qualsevol cosa.",
  },
  fr: {
    user: "Bonjour ! Pouvez-vous m'aider avec quelque chose ?",
    assistant:
      "Bien sûr ! Je suis là pour vous aider. N'hésitez pas à me demander ce que vous voulez.",
  },
  it: {
    user: "Ciao! Puoi aiutarmi con qualcosa?",
    assistant: "Certo! Sono qui per aiutarti. Sentiti libero di chiedermi qualsiasi cosa.",
  },
  pt: {
    user: "Olá! Você pode me ajudar com alguma coisa?",
    assistant:
      "Claro! Estou aqui para ajudar. Sinta-se à vontade para me perguntar qualquer coisa.",
  },
  de: {
    user: "Hallo! Können Sie mir bei etwas helfen?",
    assistant: "Natürlich! Ich bin hier, um zu helfen. Sie können mich alles fragen.",
  },
}

const MODES = {
  desktop: { width: 810, height: 640, label: "Desktop", Icon: Monitor },
  mobile: { width: 390, height: 640, label: "Mobile", Icon: Smartphone },
} as const

type Mode = keyof typeof MODES

// The iframe triggers the widget's mobile breakpoint (<800px) at narrow widths.
// On mobile, .main-element.open gets inset:0. During the 300ms close animation the
// button becomes visible while still inside that full-screen element, so it appears
// at the top-left. Keep it anchored to its configured corner during the animation.
const MOBILE_OVERRIDE = `
  @media only screen and (max-width: 800px) {
    .main-element.open:not(.fullscreen) .omni-button {
      position: absolute;
      bottom: var(--widget-pos-bottom, 20px);
      right: var(--widget-pos-right, 20px);
      left: var(--widget-pos-left, auto);
    }
  }
`

export const WidgetPreview = ({ form, language, isLoading }: Props) => {
  const { data: employee } = useAuthEmployeeQuery()
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [mode, setMode] = useState<Mode>("desktop")
  const [widgetRevision, setWidgetRevision] = useState(0)
  const config = form.watch()

  useEffect(() => {
    if (!import.meta.hot) return
    const handler = () => setWidgetRevision((r) => r + 1)
    import.meta.hot.on("widget-updated", handler)
    return () => import.meta.hot?.off("widget-updated", handler)
  }, [])

  // Scale the iframe to fit the wrapper width; shrink the container to the visible area only
  useEffect(() => {
    const wrapper = wrapperRef.current
    const container = containerRef.current
    const iframe = iframeRef.current
    if (!wrapper || !container || !iframe) return

    const { width, height } = MODES[mode]

    const update = () => {
      const scale = Math.min(1, wrapper.offsetWidth / width)
      const visibleWidth = Math.round(width * scale)
      iframe.style.transform = `scale(${scale})`
      container.style.width = `${visibleWidth}px`
      container.style.height = `${Math.round(height * scale)}px`
    }

    const ro = new ResizeObserver(update)
    ro.observe(wrapper)
    update()
    return () => ro.disconnect()
  }, [mode])

  // Re-inject the widget whenever config, language, employee, mode or loading changes
  useEffect(() => {
    if (!employee || isLoading) return

    const iframe = iframeRef.current
    if (!iframe) return

    const locale = language ?? config.languages?.at(0)?.value ?? "en"
    const langCode = locale.split("-")[0]?.toLowerCase() ?? "en"

    const previewConfig = {
      isActive: true,
      channel: "Web",
      company: {
        name: employee.company?.name ?? employee.organization.name,
        logo: employee.company?.logo ?? null,
      },
      webChatConfig: config,
      slug: "preview",
      locale,
      autoHide: false,
      isEmail: false,
      previewMessages: PREVIEW_MESSAGES[langCode] ?? PREVIEW_MESSAGES.en,
    }

    // Escape </script> to prevent early tag closure inside a script block
    const configJson = JSON.stringify(previewConfig).replace(/<\//g, "<\\/")

    const html = `<!DOCTYPE html><html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>body { margin: 0; padding: 0; }</style>
  <script>window.__PERMUT_PREVIEW_CONFIG__ = ${configJson};<\/script>
</head><body>
  <script src="/widget.js?v=${widgetRevision}"><\/script>
  <script>
    var host = document.querySelector('[data-permut-main]');
    if (host && host.shadowRoot) {
      var s = document.createElement('style');
      s.textContent = ${JSON.stringify(MOBILE_OVERRIDE)};
      host.shadowRoot.appendChild(s);
    }
  <\/script>
</body></html>`

    const doc = iframe.contentWindow?.document
    if (!doc) return

    resetPreviewWidget(iframe, "widget-preview")
    doc.open()
    doc.write(html)
    doc.close()
  }, [config, language, employee, isLoading, mode, widgetRevision])

  const { width, height } = MODES[mode]

  return (
    <div className="flex flex-col gap-3">
      <Tabs
        variant="pill"
        value={mode}
        onValueChange={(v) => setMode(v as Mode)}
        className="w-fit self-center"
      >
        <TabsList variant="pill">
          {(Object.keys(MODES) as Mode[]).map((m) => {
            const { label, Icon } = MODES[m]
            return (
              <TabsTrigger key={m} value={m} variant="pill">
                <Icon size={14} />
                {label}
              </TabsTrigger>
            )
          })}
        </TabsList>
      </Tabs>
      <div ref={wrapperRef} className="w-full flex justify-center">
        <div
          ref={containerRef}
          className="bg-secondary rounded-2xl border border-border overflow-hidden relative"
        >
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <PermutLogo className="w-2/3 opacity-[0.07]" />
          </div>
          <iframe
            ref={iframeRef}
            style={{
              width: `${width}px`,
              height: `${height}px`,
              border: 0,
              display: "block",
              transformOrigin: "top left",
            }}
            title="Widget preview"
          />
        </div>
      </div>
    </div>
  )
}
